/* eslint class-methods-use-this: 0 */
import * as algoliasearch from 'algoliasearch/lite';
import moment from 'moment';
import TettraResource from './TettraResourceAbstractSchema';
import {
    ICON, RESOURCE_MODEL, RESOURCE_TYPE, SEARCH_INDEX,
} from '../utilities/CONSTANTS';
import { INLINE_REFERENCE_TYPE, FONT_ICON } from '../tettra-editor/utilities/CONSTANTS';
import { getIndexResourceId, getResourceTypeFromModel } from '../utilities/schemaUtility';

class TettraIndex extends TettraResource {
    constructor(data) {
        super(data);
        this.data = this.constructor.transformData(data);
        this.id = this.data.id;
        this.type = this.data.type;
    }

    static transformData(data) {
        const {
            objectID,
            title,
            category_id,
            last_updated,
            url,
            model,
            icon_url,
            comment,
            commentable_id,
            commentable_type,
            commentable_title,
        } = data;

        const id = getIndexResourceId(objectID);
        const type = getResourceTypeFromModel(model, commentable_type);

        return ({
            id, title, last_updated, category_id, url, model, type, icon_url, comment, commentable_id, commentable_type, commentable_title,
        });
    }

    getId() {
        return this.id;
    }

    getTitle() {
        return this.data.title;
    }

    getSubtitle() {
        return moment.unix(this.data.last_updated).fromNow();
    }

    getIcon() {
        switch (this.data.model) {
        case RESOURCE_MODEL.PAGE:
            return ICON.PAGE;
        case RESOURCE_MODEL.QUESTION:
            return ICON.QUESTION;
        case RESOURCE_MODEL.SUGGESTION:
            return ICON.SUGGESTION;
        case RESOURCE_MODEL.EXTERNAL_RESOURCE:
            return this.data.icon_url || ICON.LINK;
        case RESOURCE_MODEL.DOCUMENT:
            return ICON.PDF_DOCUMENT;
        case RESOURCE_MODEL.CATEGORY:
            return ICON.CATEGORY;
        case RESOURCE_MODEL.FOLDER:
            return ICON.FOLDER;
        case RESOURCE_MODEL.COMMENT:
            switch (this.data.commentable_type) {
            case RESOURCE_MODEL.QUESTION:
                return ICON.COMMENT_SUGGESTION;
            case RESOURCE_MODEL.SUGGESTION:
                return ICON.COMMENT_QUESTION;
            default:
                return ICON.COMMENT_PAGE;
            }
        default:
            return ICON.UNCATEGORIZED;
        }
    }

    getCustomClass() {
        switch (this.data.model) {
        case 'App\\Models\\Page':
            return 'tettra-page';
        case 'App\\Models\\Question':
            return 'tettra-question';
        default:
            return 'tettra-page';
        }
    }

    getUrl() {
        return this.data.url;
    }

    // aka the type of resource, example "App\Models\Page"
    getModel() {
        return this.data.model;
    }

    getReferenceType() {
        switch (this.data.model) {
        case 'App\\Models\\Page':
            return INLINE_REFERENCE_TYPE.PAGE_REFERENCE;
        case 'App\\Models\\Question':
            return RESOURCE_TYPE.TETTRA_QUESTION;
        default:
            return '';
        }
    }

    getEntityData() {
        const {
            id, title, url, category_id, last_updated,
        } = this.data;

        return ({
            referenceType: this.getReferenceType(),
            title,
            url,
            id,
            metadata: {
                category_id,
                last_updated,
            },
        });
    }

    getFontIcon() {
        switch (this.data.model) {
        case 'App\\Models\\Page':
            return FONT_ICON.DOCUMENT;
        case 'App\\Models\\Question':
            return FONT_ICON.TETTRA_QUESTION;
        default:
            return FONT_ICON.DOCUMENT;
        }
    }

    isInternal() {
        return true;
    }

    static Search(searchQuery, maxSuggestions = 10, teamId = null, source = null, models = [RESOURCE_MODEL.PAGE]) {
        if (!window.algoliaAppId || !window.algoliaSearchKey) return null;

        const client = algoliasearch(window.algoliaAppId, window.algoliaSearchKey);
        const index = client.initIndex(SEARCH_INDEX);
        const modelsFilter = models.map(model => `model:${model}`).join(' OR ');

        const searchParams = {
            hitsPerPage: maxSuggestions,
            attributesToRetrieve: ['objectID', 'title', 'last_updated', 'url', 'category_id', 'model', 'icon_url', 'comment', 'commentable_id', 'commentable_type'],
            restrictSearchableAttributes: ['title'],
            filters: `(${modelsFilter}) AND __soft_deleted=0`,
        };

        if (teamId != null && source != null) {
            searchParams.analyticsTags = [`team:${teamId}`, `source:${source}`];
        }

        return new Promise((resolve, reject) => {
            index.search(searchQuery, searchParams)
                .then(({ hits }) => resolve(hits.map((data) => (new TettraIndex(data)))))
                .catch(reject);
        });
    }
}

export default TettraIndex;
